import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from 'react';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  saveCoachProfile,
  type CoachProfile,
  type CoachProfileSaveBody,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';

interface Props {
  open: boolean;
  profile: CoachProfile | null;
  onClose: () => void;
  onSaved: (profile: CoachProfile) => void;
}

type Locale = 'en' | 'ru' | 'ka';
type LocalizedText = Record<Locale, string>;

const LOCALES: { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Russian' },
  { code: 'ka', label: 'Georgian' },
];
const EMPTY_TEXT: LocalizedText = { en: '', ru: '', ka: '' };

export function CoachProfileDialog({ open, profile, onClose, onSaved }: Props) {
  const { push } = useToast();
  const [draftId, setDraftId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState<LocalizedText>(EMPTY_TEXT);
  const [availability, setAvailability] = useState<LocalizedText>(EMPTY_TEXT);
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      if (initializedRef.current) returnFocusRef.current?.focus();
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setDraftId(profile?.id ?? crypto.randomUUID());
    setName(profile?.name ?? '');
    setDescription(profile ? { ...profile.description } : { ...EMPTY_TEXT });
    setAvailability(profile ? { ...profile.availability } : { ...EMPTY_TEXT });
    setPublished(profile?.published ?? false);
    setBusy(false);
    setError(null);
    requestAnimationFrame(() => nameRef.current?.focus());
  }, [open, profile]);

  const updateLocalized = (
    setter: Dispatch<SetStateAction<LocalizedText>>,
    locale: Locale,
    value: string,
  ) => setter((current) => ({ ...current, [locale]: value }));

  const close = () => {
    if (!busy) onClose();
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !draftId) return;
    setBusy(true);
    setError(null);
    const body: CoachProfileSaveBody = {
      name,
      description,
      availability,
      published,
    };
    try {
      const saved = await saveCoachProfile(draftId, body);
      push('success', profile ? 'Coach profile updated.' : 'Coach profile created.');
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        setError('Review the name and all localized fields, then try again.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('Could not save coach profile. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={profile ? `Edit coach: ${profile.name}` : 'New coach profile'}
      maxWidth={720}
    >
      <form onSubmit={(event) => void save(event)} style={formStyle}>
        {error && <div role="alert" style={errorStyle}>{error}</div>}

        <Field label="Name">
          <input
            ref={nameRef}
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            required
            disabled={busy}
            style={inputStyle}
          />
        </Field>

        {LOCALES.map(({ code, label }) => (
          <fieldset key={code} style={fieldsetStyle}>
            <legend style={legendStyle}>{label}</legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="Description">
                <textarea
                  value={description[code]}
                  onChange={(event) => updateLocalized(setDescription, code, event.target.value)}
                  maxLength={1000}
                  rows={3}
                  disabled={busy}
                  style={textareaStyle}
                />
              </Field>
              <Field label="Availability">
                <textarea
                  value={availability[code]}
                  onChange={(event) => updateLocalized(setAvailability, code, event.target.value)}
                  maxLength={500}
                  rows={2}
                  disabled={busy}
                  style={textareaStyle}
                />
              </Field>
            </div>
          </fieldset>
        ))}

        <label style={checkboxStyle}>
          <input
            type="checkbox"
            checked={published}
            onChange={(event) => setPublished(event.target.checked)}
            disabled={busy}
          />
          Published
        </label>
        <p style={hintStyle}>
          Publishing requires a description and availability in every language.
        </p>

        <div style={actionsStyle}>
          <Button type="button" variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : profile ? 'Save changes' : 'Create profile'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  background: PS.white,
  color: PS.nearBlack,
  ...T.ui,
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  minHeight: 70,
  resize: 'vertical',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minWidth: 0,
};

const fieldsetStyle: CSSProperties = {
  minWidth: 0,
  margin: 0,
  padding: 12,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 3,
};

const legendStyle: CSSProperties = {
  ...T.subheading,
  color: PS.nearBlack,
  padding: '0 4px',
};

const checkboxStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  ...T.ui,
};

const hintStyle: CSSProperties = {
  ...T.caption,
  color: PS.textSec,
  margin: '-10px 0 0 24px',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  flexWrap: 'wrap',
};

const errorStyle: CSSProperties = {
  padding: 10,
  border: `1px solid ${PS.errorBorder}`,
  borderRadius: 3,
  color: PS.errorText,
  background: PS.errorBg,
  ...T.ui,
};
